import { useState } from "react";
import { CalendarHeatmap } from "../components/charts/CalendarHeatmap.tsx";
import { EnergyLine } from "../components/charts/EnergyLine.tsx";
import { MoodBars } from "../components/charts/MoodBars.tsx";
import { Loader } from "../components/Loader.tsx";
import { api } from "../lib/api.ts";
import { addDays, todayISO } from "../lib/day.ts";
import type { DailyPoint, Summary } from "../lib/types.ts";
import { useAsync } from "../lib/useAsync.ts";

const RANGES = [
  { days: 30, label: "30 días" },
  { days: 90, label: "3 meses" },
  { days: 365, label: "1 año" },
];

function Stat({
  label,
  value,
  unit,
}: {
  label: string;
  value: string | number | null;
  unit?: string;
}) {
  return (
    <div className="stat">
      <span className="stat__label">{label}</span>
      <span className="stat__value">
        {value ?? "—"}
        {value !== null && unit && <span className="stat__unit"> {unit}</span>}
      </span>
    </div>
  );
}

export function Progress() {
  const [days, setDays] = useState(90);
  const to = todayISO();
  const from = addDays(to, -(days - 1));

  const { data, error, loading } = useAsync<{
    daily: DailyPoint[];
    summary: Summary;
  }>(async () => {
    const [daily, summary] = await Promise.all([
      api.dailyStats({ from, to }),
      api.summary(),
    ]);
    return { daily, summary };
  }, [days]);

  return (
    <>
      <section className="card">
        <div className="card__head">
          <h2>Evolución</h2>
          <div className="segmented" role="group" aria-label="Rango">
            {RANGES.map((range) => (
              <button
                key={range.days}
                className="segmented__item"
                aria-pressed={days === range.days}
                onClick={() => setDays(range.days)}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        <Loader loading={loading} error={error}>
          {data && (
            <div className="stats">
              <Stat label="Media 7 días" value={data.summary.energyAvg7d} unit="/100" />
              <Stat label="Media 30 días" value={data.summary.energyAvg30d} unit="/100" />
              <Stat label="Días registrados" value={data.summary.daysLogged} />
              <Stat label="Registros" value={data.summary.totalEntries} />
            </div>
          )}
        </Loader>
      </section>

      <Loader loading={loading} error={error}>
        {data && (
          <>
            <section className="card">
              <div className="card__head">
                <h2>Energía en el tiempo</h2>
              </div>
              <EnergyLine points={data.daily} />
            </section>

            <section className="card">
              <div className="card__head">
                <h2>Calendario</h2>
                <span className="card__hint">Un cuadro por día</span>
              </div>
              <CalendarHeatmap points={data.daily} from={from} to={to} />
            </section>

            <section className="card">
              <div className="card__head">
                <h2>Reparto de estados de ánimo</h2>
                <span className="card__hint">Histórico completo</span>
              </div>
              <MoodBars distribution={data.summary.moodDistribution} />
            </section>
          </>
        )}
      </Loader>
    </>
  );
}
